/**
 * App instalable de THCC (sin service worker). Un solo módulo concentra lo que comparten el aviso automático
 * (snippets/thcc-pwa-instalar.liquid) y la entrada permanente "Instalar app" de los menús
 * (snippets/thcc-pwa-boton.liquid + snippets/thcc-pwa-dialogo.liquid):
 *  - captura de `beforeinstallprompt` (una sola vez, antes de que exista el DOM);
 *  - detección de plataforma (UA + capacidades): `detectarPlataforma()`;
 *  - lanzamiento del aviso nativo (`lanzarInstalacion()`), que usan ambos.
 *
 * Entrada de los menús (`[data-thcc-pwa-item]`, siempre renderizada con `hidden`): se muestra solo si la
 * plataforma puede instalar o agregar a inicio y no corre ya como app (display-mode standalone / minimal-ui /
 * fullscreen o navigator.standalone) ni hubo `appinstalled`. Lleva el ícono y el rótulo de la plataforma. Al
 * pulsarla: con `beforeinstallprompt` capturado llama a prompt(); si no, abre el diálogo de instrucciones
 * (#thcc-pwa-dialogo) con los pasos de esa plataforma. Firefox de escritorio no es instalable: queda oculta.
 *
 * Aviso automático (bottom sheet, #thcc-pwa): solo móvil (max-width: 749px) y fuera de modo app; con la mayoría
 * de edad confirmada (localStorage `thcc_edad_ok`, ver assets/thcc-edad.js) o sin aviso de edad en la página; a
 * partir de la 2.ª visita (una visita nueva = volver tras más de 30 min sin actividad, con la marca de última
 * actividad en localStorage); fuera de carrito, checkout y fichas de producto; sin cierre en los últimos 30 días;
 * y con camino de instalación (`beforeinstallprompt` capturado o Safari de iPhone/iPad). No roba el foco.
 *
 * Todo el almacenamiento va en try/catch: sin él, el aviso no se muestra (la entrada de los menús no lo necesita).
 */

const CLAVE_EDAD = 'thcc_edad_ok';
const CLAVE_VISITAS = 'thcc_pwa_visitas';
const CLAVE_ULTIMA = 'thcc_pwa_ultima';
const CLAVE_CERRADO = 'thcc_pwa_cerrado';
const VIGENCIA_EDAD_MS = 30 * 24 * 60 * 60 * 1000;
const SILENCIO_MS = 30 * 24 * 60 * 60 * 1000;
const INACTIVIDAD_MS = 30 * 60 * 1000;
const VISITAS_MINIMAS = 2;
const RETRASO_MS = 2500;

/**
 * @typedef {object} Plataforma
 * @property {string} id - Clave de la plataforma detectada
 * @property {'android' | 'apple' | 'windows' | 'computer'} icono - Ícono del botón (data-thcc-pwa-icono)
 * @property {'instalar' | 'ios' | 'dock'} etiqueta - Rótulo del botón (data-etiqueta-*)
 * @property {string} pasos - Lista de pasos del diálogo (data-thcc-pwa-pasos)
 */

/** @type {Record<string, Plataforma>} */
const PLATAFORMAS = {
  android: { id: 'android', icono: 'android', etiqueta: 'instalar', pasos: 'android' },
  'android-otro': { id: 'android-otro', icono: 'android', etiqueta: 'instalar', pasos: 'android-otro' },
  samsung: { id: 'samsung', icono: 'android', etiqueta: 'instalar', pasos: 'samsung' },
  ios: { id: 'ios', icono: 'apple', etiqueta: 'ios', pasos: 'ios' },
  'ios-otro': { id: 'ios-otro', icono: 'apple', etiqueta: 'ios', pasos: 'ios-otro' },
  windows: { id: 'windows', icono: 'windows', etiqueta: 'instalar', pasos: 'escritorio' },
  mac: { id: 'mac', icono: 'computer', etiqueta: 'instalar', pasos: 'escritorio' },
  'mac-safari': { id: 'mac-safari', icono: 'computer', etiqueta: 'dock', pasos: 'mac-safari' },
  escritorio: { id: 'escritorio', icono: 'computer', etiqueta: 'instalar', pasos: 'escritorio' },
};

/** Navegadores incrustados en apps (no permiten instalar): se reconocen por su marca en el UA. */
const APP_INCRUSTADA = /FBAN|FBAV|FB_IAB|Instagram|Line\/|MicroMessenger|Snapchat|TikTok|musical_ly|Twitter|LinkedInApp|Pinterest|WhatsApp|GSA\//;
/** Navegadores de iPhone/iPad que no son Safari (en iOS todos son WebKit, pero solo Safari instala con fiabilidad). */
const IOS_NO_SAFARI = /CriOS|FxiOS|EdgiOS|OPiOS|OPT\/|DuckDuckGo|Brave|YaBrowser|Focus\//;

/**
 * Detecta la plataforma y el navegador por UA y capacidades.
 * @param {string} ua - navigator.userAgent
 * @param {{ maxTouchPoints?: number }} [navegador] - Capacidades del navegador (táctil, para iPadOS)
 * @returns {Plataforma | null} null si ahí no se puede instalar (p. ej. Firefox de escritorio)
 */
export function detectarPlataforma(ua, navegador = {}) {
  const toques = navegador.maxTouchPoints ?? 0;
  const chromium = /Chrome\/|Chromium\/|Edg\/|EdgA\/|OPR\//.test(ua);

  // iPadOS pide por defecto el sitio de escritorio: UA de Mac, pero con pantalla táctil.
  const ipadOs = /Macintosh/.test(ua) && toques > 1;
  if (/iPhone|iPad|iPod/.test(ua) || ipadOs) {
    const safari = /Safari\//.test(ua) && !IOS_NO_SAFARI.test(ua) && !APP_INCRUSTADA.test(ua);
    return safari ? PLATAFORMAS.ios : PLATAFORMAS['ios-otro'];
  }

  if (/Android/.test(ua)) {
    if (/SamsungBrowser/.test(ua)) return PLATAFORMAS.samsung;
    if (/; wv\)/.test(ua) || APP_INCRUSTADA.test(ua)) return PLATAFORMAS['android-otro'];
    return PLATAFORMAS.android;
  }

  if (/Windows NT/.test(ua)) return chromium ? PLATAFORMAS.windows : null;

  if (/Macintosh/.test(ua)) {
    if (chromium) return PLATAFORMAS.mac;
    const version = /Version\/(\d+)/.exec(ua);
    if (version && /Safari\//.test(ua) && Number(version[1]) >= 17) return PLATAFORMAS['mac-safari']; // Archivo > Agregar al Dock
    return null; // Firefox o Safari anterior a 17
  }

  // Linux y ChromeOS: solo navegadores Chromium instalan.
  return chromium ? PLATAFORMAS.escritorio : null;
}

/** @typedef {Event & { prompt: () => Promise<void>, userChoice: Promise<{ outcome: string }> }} EventoInstalacion */

/** @type {EventoInstalacion | null} */
let instalacionDiferida = null;
let instalada = false;
let temporizador = 0;
/** @type {Set<() => void>} Quienes reaccionan a cambios de estado (la entrada de los menús y el aviso). */
const suscriptores = new Set();

function notificar() {
  for (const reevaluar of suscriptores) reevaluar();
}

const enNavegador = typeof window !== 'undefined' && typeof document !== 'undefined';
const movil = enNavegador ? window.matchMedia('(max-width: 749px)') : null;
const modoApp = enNavegador
  ? window.matchMedia('(display-mode: standalone), (display-mode: minimal-ui), (display-mode: fullscreen)')
  : null;

if (enNavegador) {
  // Debe registrarse al cargar: el evento puede llegar antes de que el DOM esté listo.
  window.addEventListener('beforeinstallprompt', (evento) => {
    evento.preventDefault(); // se usan el aviso y la entrada propios en lugar del mini-infobar
    instalacionDiferida = /** @type {EventoInstalacion} */ (evento);
    notificar();
  });

  window.addEventListener('appinstalled', () => {
    instalada = true;
    instalacionDiferida = null;
    silenciar();
    notificar();
  });
}

/** @returns {boolean} true si la página ya corre como app instalada */
function esModoApp() {
  return instalada || Boolean(modoApp?.matches) || /** @type {any} */ (navigator).standalone === true;
}

/** @returns {Plataforma | null} la plataforma instalable de este navegador; null si no aplica o ya es app */
function plataformaActual() {
  if (esModoApp()) return null;
  const ua = navigator.userAgent;
  const detectada = detectarPlataforma(ua, { maxTouchPoints: navigator.maxTouchPoints });
  if (detectada) return detectada;
  // Un navegador que ofrece el aviso nativo se puede instalar aunque su UA no se reconozca.
  if (instalacionDiferida) return /Mobi|Android/.test(ua) ? PLATAFORMAS.android : PLATAFORMAS.escritorio;
  return null;
}

/**
 * Lanza el aviso nativo de instalación (prompt() solo se puede usar una vez por evento).
 * @returns {Promise<string>} 'accepted', 'dismissed', 'error' o 'sin-aviso' si no hay `beforeinstallprompt`
 */
async function lanzarInstalacion() {
  const diferida = instalacionDiferida;
  if (!diferida) return 'sin-aviso';
  instalacionDiferida = null;
  let resultado = 'error';
  try {
    await diferida.prompt();
    resultado = (await diferida.userChoice).outcome;
  } catch {
    // el navegador rechazó el aviso: se trata como descartado
  }
  if (resultado !== 'accepted') silenciar();
  notificar();
  return resultado;
}

/** @returns {boolean} true en carrito, checkout y fichas de producto */
function paginaExcluida() {
  return /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:cart|checkouts?|products)(?:\/|$)/i.test(location.pathname);
}

function leer(clave) {
  try {
    return localStorage.getItem(clave);
  } catch {
    return null;
  }
}

function escribir(clave, valor) {
  try {
    localStorage.setItem(clave, valor);
    return true;
  } catch {
    return false;
  }
}

function silenciar() {
  escribir(CLAVE_CERRADO, String(Date.now()));
}

function edadConfirmada() {
  const guardado = Number(leer(CLAVE_EDAD));
  return guardado > 0 && Date.now() - guardado < VIGENCIA_EDAD_MS;
}

function silenciado() {
  const cerrado = Number(leer(CLAVE_CERRADO));
  return cerrado > 0 && Date.now() - cerrado < SILENCIO_MS;
}

/**
 * Cuenta la visita de esta carga. Es visita nueva la primera vez o al volver tras más de 30 min sin actividad
 * (la marca de última actividad se actualiza en cada carga y al salir o esconder la pestaña).
 * @returns {number} visitas acumuladas (0 si no hay almacenamiento)
 */
function contarVisita() {
  const ahora = Date.now();
  const ultima = Number(leer(CLAVE_ULTIMA)) || 0;
  const previas = Number(leer(CLAVE_VISITAS)) || 0;
  const nueva = ultima === 0 || ahora - ultima > INACTIVIDAD_MS;
  const total = nueva ? previas + 1 : previas;
  const guardado = escribir(CLAVE_ULTIMA, String(ahora)) && (!nueva || escribir(CLAVE_VISITAS, String(total)));

  const marcarActividad = () => {
    escribir(CLAVE_ULTIMA, String(Date.now()));
  };
  window.addEventListener('pagehide', marcarActividad);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') marcarActividad();
  });

  return guardado ? total : 0;
}

/* ---------- Entrada permanente de los menús + diálogo de instrucciones ---------- */

/** @type {Record<Plataforma['etiqueta'], string>} Atributo data-etiqueta-* del botón (clave de dataset). */
const ETIQUETAS = { instalar: 'etiquetaInstalar', ios: 'etiquetaIos', dock: 'etiquetaDock' };

/** Muestra u oculta cada entrada `[data-thcc-pwa-item]` y le pone el ícono y el rótulo de la plataforma. */
function pintarMenu() {
  const plataforma = plataformaActual();
  for (const item of document.querySelectorAll('[data-thcc-pwa-item]')) {
    if (!(item instanceof HTMLElement)) continue;
    const boton = item.querySelector('[data-thcc-pwa-menu]');
    const etiqueta = boton instanceof HTMLElement && plataforma ? boton.dataset[ETIQUETAS[plataforma.etiqueta]] : '';
    if (!(boton instanceof HTMLElement) || !plataforma || !etiqueta) {
      item.hidden = true;
      continue;
    }
    for (const icono of boton.querySelectorAll('[data-thcc-pwa-icono]')) {
      if (icono instanceof HTMLElement) icono.hidden = icono.dataset.thccPwaIcono !== plataforma.icono;
    }
    const texto = boton.querySelector('[data-thcc-pwa-etiqueta]');
    if (texto && texto.textContent !== etiqueta) texto.textContent = etiqueta;
    boton.dataset.plataforma = plataforma.id;
    item.hidden = false;
  }
}

/** @type {HTMLElement | null} Quien abrió el diálogo, para devolverle el foco al cerrar. */
let abridor = null;

/** @param {HTMLElement} disparador */
function abrirInstrucciones(disparador) {
  const dialogo = document.getElementById('thcc-pwa-dialogo');
  const plataforma = plataformaActual();
  if (!(dialogo instanceof HTMLDialogElement) || !plataforma) return;
  for (const lista of dialogo.querySelectorAll('[data-thcc-pwa-pasos]')) {
    if (lista instanceof HTMLElement) lista.hidden = lista.dataset.thccPwaPasos !== plataforma.pasos;
  }
  abridor = disparador;
  if (!dialogo.open) dialogo.showModal();
}

/** Click en la entrada: aviso nativo si hay `beforeinstallprompt`; si no, instrucciones de la plataforma. */
async function alPulsarMenu(boton) {
  if (instalacionDiferida) {
    await lanzarInstalacion();
    return;
  }
  abrirInstrucciones(boton);
}

function iniciarMenu() {
  // Delegación: funciona con la entrada del menú móvil y con la de la franja de utilidades.
  document.addEventListener('click', (evento) => {
    const objetivo = evento.target instanceof Element ? evento.target.closest('[data-thcc-pwa-menu]') : null;
    if (objetivo instanceof HTMLElement) void alPulsarMenu(objetivo);
  });

  const dialogo = document.getElementById('thcc-pwa-dialogo');
  if (dialogo instanceof HTMLDialogElement) {
    for (const cerrar of dialogo.querySelectorAll('[data-thcc-pwa-dialogo-cerrar]')) {
      cerrar.addEventListener('click', () => dialogo.close());
    }
    // Cierre al tocar el fondo: `closedby="any"` lo hace el navegador; sin él (Safari) se resuelve aquí.
    if (!('closedBy' in HTMLDialogElement.prototype)) {
      dialogo.addEventListener('click', (evento) => {
        if (evento.target !== dialogo) return;
        const caja = dialogo.getBoundingClientRect();
        const dentro =
          caja.top <= evento.clientY &&
          evento.clientY <= caja.bottom &&
          caja.left <= evento.clientX &&
          evento.clientX <= caja.right;
        if (!dentro) dialogo.close();
      });
    }
    dialogo.addEventListener('close', () => {
      if (abridor?.isConnected) abridor.focus({ preventScroll: true });
      abridor = null;
    });
  }

  suscriptores.add(pintarMenu);
  modoApp?.addEventListener('change', pintarMenu);
  pintarMenu();
}

/* ---------- Aviso automático (bottom sheet) ---------- */

/** @param {number} visitas */
function iniciarAviso(visitas) {
  const aviso = document.getElementById('thcc-pwa');
  if (!(aviso instanceof HTMLElement)) return;
  if (esModoApp() || paginaExcluida()) return;

  const botonInstalar = aviso.querySelector('[data-thcc-pwa-instalar]');
  const textoIOS = aviso.querySelector('[data-thcc-pwa-ios]');
  const botonCerrar = aviso.querySelector('[data-thcc-pwa-cerrar]');
  const dialogoEdad = document.getElementById('thcc-edad');

  function ocultar() {
    clearTimeout(temporizador);
    aviso.removeAttribute('data-abierto');
    aviso.hidden = true;
  }

  function cerrar() {
    silenciar();
    ocultar();
  }

  /** @returns {boolean} true si se cumplen todas las condiciones y hay forma de instalar */
  function puedeMostrar() {
    if (!movil?.matches || esModoApp() || silenciado()) return false;
    if (visitas < VISITAS_MINIMAS) return false;
    if (dialogoEdad instanceof HTMLDialogElement && (dialogoEdad.open || !edadConfirmada())) return false;
    return instalacionDiferida !== null || plataformaActual()?.id === 'ios';
  }

  function mostrar() {
    if (!puedeMostrar() || !aviso.hidden) return;
    const android = instalacionDiferida !== null;
    if (botonInstalar instanceof HTMLElement) botonInstalar.hidden = !android;
    if (textoIOS instanceof HTMLElement) textoIOS.hidden = android;
    aviso.hidden = false;
    void aviso.offsetWidth; // fuerza el estilo inicial para que la transición de entrada corra
    aviso.setAttribute('data-abierto', '');
  }

  /** Reevalúa con un pequeño retraso para no competir con la carga inicial. */
  function evaluar() {
    clearTimeout(temporizador);
    if (!puedeMostrar()) {
      if (!aviso.hidden) ocultar();
      return;
    }
    temporizador = window.setTimeout(mostrar, RETRASO_MS);
  }

  botonCerrar?.addEventListener('click', cerrar);

  aviso.addEventListener('keydown', (evento) => {
    if (evento.key === 'Escape') cerrar();
  });

  botonInstalar?.addEventListener('click', async () => {
    await lanzarInstalacion();
    ocultar();
  });

  suscriptores.add(evaluar);

  // Tras confirmar la edad el <dialog> se cierra: ahí se reevalúa (evento y atributo `open`, el que llegue primero).
  if (dialogoEdad) {
    dialogoEdad.addEventListener('close', evaluar);
    new MutationObserver(evaluar).observe(dialogoEdad, { attributes: true, attributeFilter: ['open'] });
  }
  movil?.addEventListener('change', evaluar);
  modoApp?.addEventListener('change', evaluar);

  evaluar();
}

function iniciar() {
  const visitas = contarVisita();
  iniciarMenu();
  iniciarAviso(visitas);
}

if (enNavegador) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', iniciar, { once: true });
  } else {
    iniciar();
  }
}
